// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignResultsStep.jsx
// Название: Campaign Results Step - Complete Professional Version
// Версия: 8.0.0 - ENHANCED COMPLETE
// Дата: 08.01.2026

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

const CampaignResultsStep = ({ campaignId, campaign, onBack, onDataUpdate }) => {
  const [gifts, setGifts] = useState([]);
  const [stats, setStats] = useState({ total: 0, ready: 0, distributed: 0, claimed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const GIFTS_PER_PAGE = 12;
  const totalPages = Math.ceil(gifts.length / GIFTS_PER_PAGE);
  const startIndex = (currentPage - 1) * GIFTS_PER_PAGE;
  const currentGifts = gifts.slice(startIndex, startIndex + GIFTS_PER_PAGE);

  // Действия
  const [distributing, setDistributing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [previewGift, setPreviewGift] = useState(null);

  console.log('🎯 CampaignResultsStep STARTED');

  const loadResults = useCallback(async () => {
    if (!campaignId) return;
    
    setLoading(true);
    console.log('🎯 loadResults called, campaignId:', campaignId);
    setError('');
    try {
      const response = await api.get(`/campaigns/${campaignId}/results`);
      const data = response.data || response;
      
      const giftsData = data.gifts || data.data?.gifts || [];
      const statsData = data.stats || data.data?.stats || {};
      
      setGifts(giftsData);
      setStats({
        total: parseInt(statsData.total) || giftsData.length,
        ready: parseInt(statsData.ready) || giftsData.filter(g => !g.distributed_at && !g.claimed_at).length,
        distributed: parseInt(statsData.distributed) || giftsData.filter(g => g.distributed_at && !g.claimed_at).length,
        claimed: parseInt(statsData.claimed) || giftsData.filter(g => g.claimed_at).length
      });
      
      console.log('✅ Results loaded successfully');
    } catch (err) {
      console.error('❌ Load results error:', err);
      setError(`Ошибка загрузки: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    console.log('🎯 useEffect triggered, campaignId:', campaignId);
    loadResults();
  }, [loadResults]);

  // ============================================
  // ДЕЙСТВИЯ
  // ============================================
  const handleDistributeEmail = async () => {
    const toSend = selectedGifts.length > 0 
      ? selectedGifts 
      : currentGifts.filter(g => g.email && !g.distributed_at).map(g => g.gift_id || g.id);
    
    if (toSend.length === 0) {
      setError('❌ Нет подарков для отправки');
      return;
    }
    
    if (!window.confirm(`Отправить ${toSend.length} подарков по Email?`)) return;

    setDistributing(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(`/campaigns/${campaignId}/distribute`, {
        method: 'email',
        giftIds: toSend
      });
      
      const sent = response.data?.sent || toSend.length;
      setSuccess(`✅ Отправлено: ${sent} ${sent === 1 ? 'письмо' : sent < 5 ? 'письма' : 'писем'}`);
      
      await loadResults();
      setSelectedGifts([]);
    } catch (err) {
      setError(`❌ Ошибка отправки: ${err.response?.data?.error || err.message}`);
    } finally {
      setDistributing(false);
    }
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    setError('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com/api'}/campaigns/${campaignId}/download-zip`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign_${campaignId}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      setSuccess('✅ ZIP архив скачан');
    } catch (err) {
      setError(`❌ Ошибка скачивания: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const claimedAmount = stats.claimed * (parseFloat(campaign?.reward_amount) || 25);
      const claimRate = stats.total > 0 ? Math.round((stats.claimed / stats.total) * 100) : 0;
      
      const shareData = {
        title: `${campaign?.name || 'AI Подарки'} - DARY`,
        text: `🎁 AI-подарки созданы! ${stats.total} подарков, ${stats.claimed} активировано (${claimRate}%), ${claimedAmount.toFixed(0)} DAR выдано`,
        url: window.location.origin
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        setSuccess('✅ Поделился результатами');
      } else {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        setSuccess('✅ Скопировано в буфер');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(`❌ Ошибка: ${err.message}`);
      }
    } finally {
      setSharing(false);
    }
  };

  const handleFinishCampaign = async () => {
    if (!window.confirm('Завершить кампанию?')) return;
    setFinishing(true);
    try {
      await api.put(`/campaigns/${campaignId}`, { status: 'completed' });
      setSuccess('✅ Кампания завершена');
      await loadResults();
    } catch (err) {
      setError(`❌ Ошибка: ${err.response?.data?.error || err.message}`);
    } finally {
      setFinishing(false);
    }
  };

  // Прямое скачивание изображения
  const handleDirectDownload = async (gift, e) => {
    e.stopPropagation();
    const imageUrl = gift.image_url || gift.gift_image_url;
    if (!imageUrl) return;
    
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${gift.name || 'gift'}_${gift.short_code || gift.gift_id}.png`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      // Fallback - открыть в новой вкладке
      window.open(imageUrl, '_blank');
    }
  };

  // ============================================
  // ХЕЛПЕРЫ
  // ============================================
  const toggleSelectGift = (giftId) => {
    setSelectedGifts(prev => 
      prev.includes(giftId) ? prev.filter(id => id !== giftId) : [...prev, giftId]
    );
  };

  const selectAll = () => {
    const allCurrentIds = currentGifts.map(g => g.gift_id || g.id);
    setSelectedGifts(selectedGifts.length === allCurrentIds.length ? [] : allCurrentIds);
  };

  const getStatusBadge = (gift) => {
    if (gift.claimed_at || gift.status === 'claimed') {
      return { text: '✅', color: '#059669', bg: '#dcfce7' };
    }
    if (gift.distributed_at) {
      return { text: '📧', color: '#1e40af', bg: '#dbeafe' };
    }
    return { text: '⏳', color: '#92400e', bg: '#fef3c7' };
  };

  const getTotalAmount = () => (stats.claimed * (parseFloat(campaign?.reward_amount) || 25)).toFixed(0);
  const getClaimRate = () => stats.total > 0 ? Math.round((stats.claimed / stats.total) * 100) : 0;

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div style={{textAlign: 'center', padding: '60px', color: '#6b7280'}}>
        <div style={{
          width: '40px', height: '40px', border: '3px solid #f3f4f6', 
          borderTopColor: '#10B981', borderRadius: '50%', 
          animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
        }}></div>
        ⏳ Загрузка результатов...
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{maxWidth: '1400px', margin: '0 auto', padding: '20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'}}>
      {/* Messages */}
      {error && (
        <div style={{background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', fontWeight: '500'}}>
          {error}
        </div>
      )}
      {success && (
        <div style={{background: '#f0fdf4', color: '#059669', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', fontWeight: '500'}}>
          {success}
        </div>
      )}

      {/* Main Stats & Actions Panel */}
      <div style={{
        background: 'white', padding: '24px', borderRadius: '12px', 
        marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
        flexWrap: 'wrap', gap: '20px'
      }}>
        <div style={{flex: 1, minWidth: '300px'}}>
          <div style={{fontSize: '16px', color: '#374151', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px'}}>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
              📊 <strong style={{color: '#10B981', fontSize: '18px'}}>{stats.total}</strong> {stats.total === 1 ? 'подарок' : 'подарков'}
            </span>
            <span style={{color: '#9ca3af'}}>•</span>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
              ✅ <strong style={{color: '#059669', fontSize: '18px'}}>{stats.claimed}</strong> активировано ({getClaimRate()}%)
            </span>
            <span style={{color: '#9ca3af'}}>•</span>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
              💰 <strong style={{color: '#10B981', fontSize: '18px'}}>{getTotalAmount()} DAR</strong> выдано
            </span>
          </div>
        </div>

        <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
          <button 
            onClick={handleDistributeEmail}
            disabled={distributing}
            style={{
              background: '#10B981', color: 'white', border: 'none',
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
              cursor: distributing ? 'not-allowed' : 'pointer',
              opacity: distributing ? 0.6 : 1, transition: 'all 0.2s ease',
              display: 'inline-flex', alignItems: 'center', gap: '6px'
            }}
          >
            {distributing ? '⏳' : '📧'} Email
          </button>
          <button 
            onClick={handleDownloadZip}
            disabled={downloading}
            style={{
              background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
              cursor: downloading ? 'not-allowed' : 'pointer',
              opacity: downloading ? 0.6 : 1, transition: 'all 0.2s ease',
              display: 'inline-flex', alignItems: 'center', gap: '6px'
            }}
          >
            {downloading ? '⏳' : '📥'} ZIP
          </button>
          <button 
            onClick={handleShare}
            disabled={sharing}
            style={{
              background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
              cursor: sharing ? 'not-allowed' : 'pointer',
              opacity: sharing ? 0.6 : 1, transition: 'all 0.2s ease',
              display: 'inline-flex', alignItems: 'center', gap: '6px'
            }}
          >
            {sharing ? '⏳' : '📱'} Share
          </button>
          <button 
            onClick={loadResults}
            style={{
              background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
              padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.2s ease',
              display: 'inline-flex', alignItems: 'center', gap: '6px'
            }}
          >
            🔄 Обновить
          </button>
        </div>
      </div>

      {/* Gifts Section */}
      <div style={{background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px'}}>
          <h3 style={{margin: 0, fontSize: '20px', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px'}}>
            🎁 Подарки ({gifts.length})
            {totalPages > 1 && (
              <span style={{fontSize: '16px', color: '#6b7280', fontWeight: '500'}}>
                • Страница {currentPage} из {totalPages}
              </span>
            )}
            {selectedGifts.length > 0 && (
              <span style={{color: '#10B981', fontSize: '16px', fontWeight: '500'}}>
                • {selectedGifts.length} выбрано
              </span>
            )}
          </h3>
          
          {currentGifts.length > 1 && (
            <button 
              onClick={selectAll}
              style={{
                background: 'none', border: '2px solid #10B981', color: '#10B981',
                padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {e.target.style.background = '#10B981'; e.target.style.color = 'white';}}
              onMouseLeave={e => {e.target.style.background = 'none'; e.target.style.color = '#10B981';}}
            >
              {selectedGifts.length === currentGifts.length ? 'Снять всё' : 'Выбрать всё'}
            </button>
          )}
        </div>

        {gifts.length === 0 ? (
          <div style={{textAlign: 'center', padding: '60px', color: '#9ca3af'}}>
            <div style={{fontSize: '48px', marginBottom: '16px', opacity: 0.5}}>🎁</div>
            <h3 style={{fontSize: '18px', margin: '0 0 8px 0'}}>Подарки не найдены</h3>
            <p style={{margin: 0, fontSize: '14px'}}>Возможно, кампания еще не завершена или произошла ошибка</p>
          </div>
        ) : (
          <div style={{
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
			justifyContent: 'start',
            marginBottom: totalPages > 1 ? '24px' : '0'
          }}>
            {currentGifts.map(gift => {
              const giftId = gift.gift_id || gift.id;
              const isSelected = selectedGifts.includes(giftId);
              const status = getStatusBadge(gift);
              const imageUrl = gift.image_url || gift.gift_image_url;
              
              return (
                <div 
                  key={giftId}
                  onClick={() => toggleSelectGift(giftId)}
                  style={{
                    background: isSelected ? '#f0fdf4' : '#f9fafb',
                    border: `2px solid ${isSelected ? '#10B981' : 'transparent'}`,
                    borderRadius: '12px', cursor: 'pointer', overflow: 'hidden',
                    transition: 'all 0.2s ease', 
                    display: 'flex', flexDirection: 'column',
                    boxShadow: isSelected ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 4px rgba(0,0,0,0.05\),',
					maxWidth: '400px',
					width: '100%' 
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05\),';
                    }
                  }}
                >
                  {/* Image Section */}
                  <div style={{position: 'relative', height: '160px', overflow: 'hidden'}}>
                    {imageUrl && (
                      <img 
                        src={imageUrl} 
                        alt={gift.name}
                        style={{width: '100%', height: '100%', objectFit: 'cover'}}
                        onClick={(e) => { e.stopPropagation(); setPreviewGift(gift); }}
                      />
                    )}
                    
                    {/* Status Badge */}
                    <div style={{
                      position: 'absolute', top: '8px', right: '8px',
                      background: status.bg, color: status.color, padding: '4px 8px',
                      borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {status.text} {gift.claimed_at ? 'Активирован' : gift.distributed_at ? 'Отправлен' : 'Готов'}
                    </div>

                    {/* Download Button */}
                    {imageUrl && (
                      <button
                        onClick={(e) => handleDirectDownload(gift, e)}
                        style={{
                          position: 'absolute', bottom: '8px', right: '8px',
                          background: 'rgba(16, 185, 129, 0.9)', color: 'white', border: 'none',
                          padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                          cursor: 'pointer', transition: 'all 0.2s ease',
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                        onMouseEnter={e => e.target.style.background = '#059669'}
                        onMouseLeave={e => e.target.style.background = 'rgba(16, 185, 129, 0.9)'}
                      >
                        📥 Скачать
                      </button>
                    )}
                  </div>
                  
                  {/* Info Section */}
                  <div style={{padding: '16px', display: 'flex', flexDirection: 'column', flex: 1}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px'}}>
                      <h4 style={{
                        margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937',
                        overflow: 'hidden', textOverflow: 'ellipsis', 
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        lineHeight: '1.3', flex: 1
                      }}>
                        {gift.name || 'Получатель'}
                      </h4>
                      <span style={{
                        fontSize: '14px', fontWeight: '700', color: '#10B981', 
                        marginLeft: '8px', whiteSpace: 'nowrap'
                      }}>
                        {parseFloat(gift.amount || 0).toFixed(0)} DAR
                      </span>
                    </div>
                    
                    {/* Contact Info */}
                    <div style={{marginBottom: '12px', fontSize: '13px', color: '#6b7280', lineHeight: '1.4'}}>
                      <div style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px'}}>
                        📧 {gift.email || '—'}
                      </div>
                      {gift.phone && (
                        <div style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          📱 {gift.phone}
                        </div>
                      )}
                      {gift.short_code && (
                        <div style={{fontSize: '11px', color: '#9ca3af', fontFamily: 'Monaco, Consolas, monospace', marginTop: '4px'}}>
                          🎫 {gift.short_code}
                        </div>
                      )}
                    </div>

                    {/* Activation Info - Detailed */}
                    {gift.claimed_by_name && (
                      <div style={{
                        background: 'linear-gradient(135deg, #dcfce7, #f0fdf4)', 
                        border: '1px solid #bbf7d0', padding: '12px', borderRadius: '8px',
                        fontSize: '12px', color: '#166534', marginTop: 'auto'
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
                          <span style={{fontSize: '14px', marginRight: '6px'}}>👤</span>
                          <strong style={{fontSize: '13px'}}>
                            Активировано пользователем:
                          </strong>
                        </div>
                        <div style={{marginLeft: '20px', lineHeight: '1.4'}}>
                          <div style={{fontWeight: '600', color: '#047857', marginBottom: '2px'}}>
                            {gift.claimed_by_name}
                          </div>
                          {gift.claimed_by_email && gift.claimed_by_email !== gift.email && (
                            <div style={{fontSize: '11px', color: '#059669', marginBottom: '2px'}}>
                              📧 {gift.claimed_by_email}
                            </div>
                          )}
                          <div style={{fontSize: '11px', color: '#059669', display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span>📅 {new Date(gift.claimed_at).toLocaleDateString('ru-RU')}</span>
                            <span>🕒 {new Date(gift.claimed_at).toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Distribution Info */}
                    {gift.distributed_at && !gift.claimed_at && (
                      <div style={{
                        background: 'linear-gradient(135deg, #dbeafe, #eff6ff)', 
                        border: '1px solid #bfdbfe', padding: '10px', borderRadius: '8px',
                        fontSize: '12px', color: '#1e40af', marginTop: 'auto'
                      }}>
                        <div style={{fontWeight: '600', marginBottom: '4px'}}>
                          📧 Отправлено получателю
                        </div>
                        <div style={{fontSize: '11px', color: '#3b82f6'}}>
                          📅 {new Date(gift.distributed_at).toLocaleDateString('ru-RU')} в {new Date(gift.distributed_at).toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', paddingTop: '20px', borderTop: '1px solid #f1f5f9'}}>
            <button 
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '10px 16px', 
                background: currentPage === 1 ? '#f3f4f6' : '#10B981',
                color: currentPage === 1 ? '#9ca3af' : 'white', 
                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ← Предыдущая
            </button>
            
            <span style={{fontSize: '14px', color: '#6b7280', padding: '0 16px', fontWeight: '500'}}>
              Страница {currentPage} из {totalPages}
            </span>
            
            <button 
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '10px 16px', 
                background: currentPage === totalPages ? '#f3f4f6' : '#10B981',
                color: currentPage === totalPages ? '#9ca3af' : 'white', 
                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Следующая →
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb'}}>
        <button 
          onClick={onBack}
          style={{
            padding: '12px 24px', background: '#f8fafc', color: '#64748b',
            border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
            cursor: 'pointer', transition: 'all 0.2s ease',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}
          onMouseEnter={e => {e.target.style.background = '#f1f5f9'; e.target.style.color = '#475569';}}
          onMouseLeave={e => {e.target.style.background = '#f8fafc'; e.target.style.color = '#64748b';}}
        >
          ← К кампаниям
        </button>
        
        <button 
          onClick={handleFinishCampaign}
          disabled={finishing}
          style={{
            padding: '12px 24px', 
            background: finishing ? '#9333ea' : 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
            color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
            cursor: finishing ? 'not-allowed' : 'pointer',
            opacity: finishing ? 0.7 : 1, transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}
        >
          {finishing ? '⏳ Сохранение...' : '✅ Завершить кампанию'}
        </button>
      </div>

      {/* Preview Modal */}
      {previewGift && (
        <div 
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
          }}
          onClick={() => setPreviewGift(null)}
        >
          <div 
            style={{
              background: 'white', borderRadius: '16px', maxWidth: '600px', width: '100%',
              maxHeight: '90vh', overflow: 'auto', 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f3f4f6'}}>
              <h3 style={{margin: 0, fontSize: '20px', fontWeight: '700', color: '#1f2937'}}>{previewGift.name}</h3>
              <button 
                onClick={() => setPreviewGift(null)}
                style={{
                  background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', 
                  cursor: 'pointer', padding: '4px', borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={e => e.target.style.color = '#6b7280'}
                onMouseLeave={e => e.target.style.color = '#9ca3af'}
              >
                ×
              </button>
            </div>
            
            {previewGift.image_url && (
              <img 
                src={previewGift.image_url}
                alt={previewGift.name}
                style={{width: '100%', maxHeight: '400px', objectFit: 'contain', background: '#f9fafb'}}
              />
            )}
            
            <div style={{padding: '20px 24px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px'}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <div>
                    <span style={{color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Email</span>
                    <div style={{fontWeight: '600', color: '#1f2937'}}>{previewGift.email}</div>
                  </div>
                  <div>
                    <span style={{color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Сумма</span>
                    <div style={{fontWeight: '600', color: '#10B981'}}>{parseFloat(previewGift.amount || 0).toFixed(0)} DAR</div>
                  </div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <div>
                    <span style={{color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Статус</span>
                    <div style={{fontWeight: '600', color: '#1f2937'}}>{getStatusBadge(previewGift).text} {previewGift.claimed_at ? 'Активирован' : 'Готов'}</div>
                  </div>
                  {previewGift.short_code && (
                    <div>
                      <span style={{color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Код</span>
                      <div style={{fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px', color: '#6b7280'}}>{previewGift.short_code}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div style={{display: 'flex', gap: '12px', padding: '16px 24px', borderTop: '1px solid #f3f4f6', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setPreviewGift(null)}
                style={{
                  padding: '10px 20px', background: '#f1f5f9', color: '#475569', 
                  border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
                  cursor: 'pointer', transition: 'all 0.2s ease'
                }}
              >
                Закрыть
              </button>
              {previewGift.image_url && (
                <button
                  onClick={(e) => handleDirectDownload(previewGift, e)}
                  style={{
                    padding: '10px 20px', background: '#10B981', color: 'white', 
                    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  📥 Скачать оригинал
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignResultsStep;
